import hostClient from '../../host/HostClient';
import { IThemeOptions } from '../IThemeOptions';
import { IThemeImageOptions, IThemeObjectOptions } from '../types';

/**Generates the theme using the theming options */
export function generateThemeOptions(
    { primary, secondary, error, inputBackground, inputFontColour, paper, infoText, custom }: IThemeObjectOptions,
    imageOptions?: IThemeImageOptions,
): IThemeOptions {
    return {
        overrides: {
            MuiButton: {
                root: {
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    textTransform: 'capitalize',
                    fontFamily:
                        "-apple-system, BlinkMacSystemFont, 'Open Sans', 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif !important",
                    padding: '0.4rem 2rem',
                },
                label: {
                    fontWeight: 600,
                },
                contained: {
                    boxShadow: 'none',
                    backgroundColor: '#F3F4F9',
                    color: '#212121',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
            MuiPaper: {
                rounded: {
                    borderRadius: '16px',
                },
                elevation1: {
                    boxShadow: 'none',
                    borderRadius: '16px',
                },
            },
            MuiBackdrop: {
                root: {
                    backdropFilter: 'blur(5px)',
                },
            },
            MuiGrid: {
                root: {
                    '& .formErrors': {
                        fontSize: '0.85rem',
                        position: 'absolute',
                    },
                },
            },
            MuiInput: {
                underline: {
                    '&::before': {
                        borderBottom: 'none !important',
                    },
                },
            },
            MuiCheckbox: {
                // '& checked': {
                //     color: primary.main,
                // },
                checked: {
                    color: primary.main,
                },
            },
            MuiSelect: {
                selectMenu: {
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: inputBackground,
                },
                root: {},
            },
            MuiChip: {
                root: {},
                deleteIcon: {
                    marginRight: '3px',
                },
            },
            MuiDrawer: {
                paperAnchorDockedLeft: {
                    borderRight: 'none',
                },
            },
            MuiOutlinedInput: {
                root: {
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    padding: '0',
                    border: `1px solid ${inputBackground}`,
                    transition: '0.2s ease-in-out',
                    backgroundColor: inputBackground,
                    '&.Mui-focused': {
                        backgroundColor: paper,
                        border: `1px solid ${primary.main}`,
                        boxShadow: `0px 0px 3px ${primary.main}99`,
                    },
                    '&$error': {
                        border: `1px solid ${error}`,
                    },
                },
                notchedOutline: {
                    border: 'none',
                },
                input: {
                    height: 'calc(1rem - 2px)',
                    padding: '1rem',
                    '&::placeholder': {
                        color: infoText,
                        opacity: '1 !important',
                    },
                    '&.MuiOutlinedInput-inputMultiline': {
                        height: 'auto !important',
                    },
                },
            },
            MuiInputBase: {
                root: {
                    '&.MuiAutocomplete-inputRoot': {
                        padding: '0.25rem !important',
                    },
                },
            },
            MuiInputLabel: {
                root: {
                    transform: 'none',
                    position: 'relative',
                    paddingBottom: '10px',
                    top: '0',
                    left: '6px',
                    color: inputFontColour,
                },
                formControl: {
                    transform: 'none',
                    position: 'relative',
                    paddingBottom: '10px',
                    top: '0',
                    left: '6px',
                    color: inputFontColour,
                    '&.Mui-focused': {
                        color: inputFontColour,
                    },
                },
                outlined: {
                    transform: 'none',
                },
                shrink: {
                    transform: 'none !important',
                },
            },
        },
        palette: {
            primary,
            secondary,
            background: {
                default: '#F3F4F9',
                paper: '#ffffff',
            },
            text: {
                primary: '#2C2C2C',
                secondary: '#737FA4',
            },
        },
        typography: {
            fontFamily: 'inherit',
        },
        custom: {
            color: {
                bgGreyMid: '#E8EAF4',
            },
            ...custom,
        },
        images: {
            ...imageOptions,
        },
    };
}

/**Generates the default theme options */
export function generateDefaultThemeOptions(): IThemeObjectOptions {
    return {
        primary: { main: '#FFDD02', contrastText: '#212121' },
        secondary: { main: '#4F3CC9', contrastText: '#FFFFFF' },
        error: '#F52D5C',
        inputBackground: '#F3F4F9',
        inputFontColour: '#757575',
        paper: '#FFFFFF',
        infoText: '#AAB6D3',
    };
}

export function generateBarclayCardTheme() {
    return {
        primary: { main: '#085DA9', contrastText: '#FFFFFF' },
    };
}

export function generateRmsTheme() {
    return {
        primary: { main: '#85191C', contrastText: '#FFFFFF' },
    };
}

export function generateCardSaverTheme() {
    return {
        primary: { main: '#4a8ecc', contrastText: '#FFFFFF' },
        custom: {
            sidebar: {
                logoHeight: '117px',
                logoWidth: '394px',
            },
        },
    };
}

export function generateEposNowTheme() {
    return {
        primary: { main: '#292936', contrastText: '#FFFFFF' },
        custom: {
            sidebar: {
                logoHeight: '120px',
            },
        },
    };
}

export function generateWorldPayTheme() {
    return {
        primary: { main: '#002834', contrastText: '#FFFFFF' },
        custom: {
            sidebar: {
                logoHeight: '85px',
            },
        },
    };
}

export function generateZeroBubbleTheme() {
    return {
        primary: { main: '#F47F20', contrastText: '#FFFFFF' },
        custom: {
            sidebar: {
                logoHeight: '120px',
            },
        },
    };
}

export function generateBNATheme() {
    return {
        primary: { main: '#7FC6C7', contrastText: '#27183E' },
        custom: {
            sidebar: {
                logoHeight: '120px',
            },
        },
    };
}

/**Generates the default images */
export function generateDefaultImages() {
    const hostPartner = hostClient.host;
    let host = '';
    /**If localhost then just set it to dev */
    if (hostPartner === 'partners') {
        host = 'dev-partners-lrk0i9.yoello.com';
    } else {
        host = hostPartner;
    }
    return {
        logo: `https://s3-eu-west-1.amazonaws.com/merchant-dashboard-dev.yoello.co-assets/${host}/logo.png`,
    };
}
